import {
  Component,
  Input,
  Output,
  EventEmitter,
  OnInit,
  OnChanges,
  SimpleChanges,
  ViewChild,
  ElementRef
} from '@angular/core';

import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, Validators, ReactiveFormsModule } from '@angular/forms';

import { DialogModule } from 'primeng/dialog';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { DropdownModule } from 'primeng/dropdown';
import { TabViewModule } from 'primeng/tabview';
import { ToastModule } from 'primeng/toast';
import { MessageService } from 'primeng/api';

import { ApiService } from '../../../../services/api.service';

@Component({
  selector: 'app-add-edit-ticket-modal',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    DialogModule,
    ButtonModule,
    InputTextModule,
    DropdownModule,
    TabViewModule,
    ToastModule
  ],
  templateUrl: './add-edit-ticket-modal.component.html'
})
export class AddEditTicketModalComponent implements OnInit, OnChanges {

  @Input() visible: boolean = false;
  @Input() ticket: any;

  @Output() close = new EventEmitter();
  @Output() save = new EventEmitter();

  @ViewChild('descriptionEditor') descriptionEditor!: ElementRef;

  ticketForm!: FormGroup;
  activeTabIndex = 0;

  proposalOptions: any[] = [];
  proposalStepOptions: any[] = [];

  constructor(
    private fb: FormBuilder,
    private api: ApiService,
    private messageService: MessageService
  ) {
    this.initializeForm();
  }

  ngOnInit() {}

  ngOnChanges(changes: SimpleChanges) {

    if (changes['visible'] && this.visible) {

      this.loadProposals();

      if (this.ticket) {

        setTimeout(() => {
          this.populateForm();
        }, 300);

      } else {

        this.resetForm();

      }

    }

  }

  priorityOptions = [
    { label: 'Low', value: 'low' },
    { label: 'Medium', value: 'medium' },
    { label: 'High', value: 'high' },
    { label: 'Urgent', value: 'urgent' }
  ];

  categoryOptions = [
    { label: 'Technical Issue', value: 'technical' },
    { label: 'Functional Issue', value: 'functional' },
    { label: 'Enhancement', value: 'enhancement' },
    { label: 'Bug', value: 'bug' },
    { label: 'General', value: 'general' }
  ];

  initializeForm() {

    this.ticketForm = this.fb.group({

      title: ['', Validators.required],
      description: [''],
      category: [this.categoryOptions[0].value, Validators.required],
      priority: [this.priorityOptions[0].value, Validators.required],
      proposal: [null],
      proposalStep: [null]

    });

  }

  /* ======================
      POPULATE EDIT FORM
  ======================= */

  populateForm() {

    if (!this.ticket) return;

    const category = this.ticket.category?.toLowerCase();
    const priority = this.ticket.priority?.toLowerCase();

    this.ticketForm.patchValue({

      title: this.ticket.title || '',
      category: category || this.categoryOptions[0].value,
      priority: priority || this.priorityOptions[0].value,
      proposal: this.ticket.proposal || null

    });

    setTimeout(() => {

      if (this.descriptionEditor) {

        this.descriptionEditor.nativeElement.innerHTML =
          this.ticket.description || '';

        this.ticketForm.patchValue({
          description: this.ticket.description || ''
        });

      }

    });

    if (this.ticket.proposal) {

      this.loadProposalSteps(
        this.ticket.proposal,
        this.ticket.proposal_step
      );

    }

  }

  /* ======================
      RESET FORM
  ======================= */

  resetForm() {

    this.ticketForm.reset({

      category: this.categoryOptions[0].value,
      priority: this.priorityOptions[0].value

    });

    this.activeTabIndex = 0;
    this.proposalStepOptions = [];

    setTimeout(() => {

      if (this.descriptionEditor) {
        this.descriptionEditor.nativeElement.innerHTML = '';
      }

    });

  }

  /* ======================
      DESCRIPTION INPUT
  ======================= */

  onDescriptionInput() {

    const content = this.descriptionEditor.nativeElement.innerHTML;

    this.ticketForm.patchValue({
      description: content
    });

  }

  /* ======================
      SUBMIT
  ======================= */

  onSubmit() {

    if (this.ticketForm.invalid) {

      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: 'Please fill required fields'
      });

      return;

    }

    const description = this.descriptionEditor.nativeElement.innerHTML;

    this.ticketForm.patchValue({
      description: description
    });

    const formValue = this.ticketForm.value;

    const payload: any = {

      title: formValue.title,
      description: formValue.description,
      category: formValue.category,
      priority: formValue.priority,
      status: 'open',
      proposal: formValue.proposal || null,
      proposal_step: formValue.proposalStep || null

    };

    const request = this.ticket
      ? this.api.putAPI(`/ticket/details/${this.ticket.id}/`, payload)
      : this.api.postAPI('/ticket/details/', payload);

    request.subscribe({

      next: (res: any) => {

        this.messageService.add({
          severity: 'success',
          summary: 'Success',
          detail: this.ticket
            ? 'Ticket updated successfully'
            : 'Ticket created successfully'
        });

        this.save.emit(res.data);

        this.resetForm();
        this.close.emit();

      },

      error: () => {

        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Operation failed'
        });

      }

    });

  }

  onCancel() {

    this.resetForm();
    this.close.emit();

  }

  onHide() {

    this.resetForm();
    this.close.emit();

  }

  /* ======================
      LOAD PROPOSALS
  ======================= */

  loadProposals() {

    this.api.getAPI('/master/proposals/').subscribe((res: any) => {

      this.proposalOptions = res.data.map((p: any) => ({
        label: p.name,
        value: p.id
      }));

      if (!this.ticket && this.proposalOptions.length) {

        const firstProposal = this.proposalOptions[0].value;

        this.ticketForm.patchValue({
          proposal: firstProposal
        });

        this.loadProposalSteps(firstProposal);

      }

    });

  }

  /* ======================
      PROPOSAL CHANGE
  ======================= */

  onProposalChange(event: any) {

    const proposalId = event.value;

    this.ticketForm.patchValue({
      proposalStep: null
    });

    this.loadProposalSteps(proposalId);

  }

  /* ======================
      LOAD PROPOSAL STEPS
  ======================= */

  loadProposalSteps(proposalId: number, selectedStep?: number) {

    this.api
      .getAPI(`/transaction/copy/proposals/${proposalId}/steps/`)
      .subscribe((res: any) => {

        const steps = res.data || res;

        this.proposalStepOptions = steps.map((s: any) => ({
          label: s.name,
          value: s.id
        }));

        if (selectedStep) {

          setTimeout(() => {

            this.ticketForm.patchValue({
              proposalStep: selectedStep
            });

          }, 200);

        } else if (this.proposalStepOptions.length) {

          this.ticketForm.patchValue({
            proposalStep: this.proposalStepOptions[0].value
          });

        }

      });

  }

}